import React from 'react';
import type {FC, ReactNode} from 'react';

import {ConfirmDialog, ConfirmDialogData} from '@yandex-data-ui/cloud-components';

import {i18n} from './i18n';

interface ConfirmData {
    actionId: string;
    message: ReactNode;
    actionHandler: ConfirmDialogData['actionHandler'];

    onRequestClose?: () => void;
    title?: string;
    confirmation?: ReactNode;
    actionText?: string;
    actionTheme?: ConfirmDialogData['actionTheme'];
    cancelText?: string;
}

interface Confirmation {
    (data: ConfirmData): Promise<string>;
}

interface ConfirmProviderProps {
    children?: ReactNode;
}

export const ConfirmContext = React.createContext<Confirmation>(
    undefined as unknown as Confirmation,
);

export const ConfirmProvider: FC<ConfirmProviderProps> = ({children}) => {
    const [dialogData, setDialogData] = React.useState<ConfirmData>();
    const promiseResolve = React.useRef<(data: string) => void>(() => {});
    const promiseReject = React.useRef<(data: string) => void>(() => {});

    function confirm(data: ConfirmData) {
        return new Promise<string>((resolve, reject) => {
            promiseResolve.current = resolve;
            promiseReject.current = reject;
            setDialogData(data);
        });
    }

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {dialogData ? (
                <ConfirmDialog
                    open
                    data={{
                        cancelText: i18n('cancel-text'),
                        actionText: i18n('action-text'),
                        ...dialogData,
                    }}
                    onRequestClose={() => {
                        dialogData.onRequestClose?.();
                        return setDialogData(undefined);
                    }}
                    onSuccess={(actionId) => promiseResolve.current(actionId || '')}
                    onError={(_, actionId) => promiseReject.current(actionId || '')}
                />
            ) : null}
        </ConfirmContext.Provider>
    );
};
